import { esc } from '../render/escape.js';
import { download } from '../lib/export.js';
import Papa from 'papaparse';

const PAGE_SIZE = 50;
const display = value => value === null ? 'null' : typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
const bytes = n => n >= 1048576 ? `${(n / 1048576).toFixed(1)} MiB` : n >= 1024 ? `${(n / 1024).toFixed(1)} KiB` : `${n} B`;
const typeOf = (schema, name) => {
  const field = schema.find(f => f.name === name);
  if (!field) return 'partition';
  return field.logical_type ? JSON.stringify(field.logical_type) : field.converted_type || field.type || 'group';
};

// Chrome fragments shared by both layouts, so the two presentations can never
// describe the same document differently.
const metaLine = doc => `<b>Parquet${doc.dataset ? ' dataset' : ''}</b>${doc.files ? ` · ${doc.files.length} file${doc.files.length === 1 ? '' : 's'}` : ''} · ${doc.rowCount.toLocaleString()} rows · ${doc.columns.length} columns · ${doc.rowGroups} row ${doc.rowGroups === 1 ? 'group' : 'groups'} · ${esc(doc.codecs.join(', ') || 'empty')}`;
const filesBlock = doc => doc.files ? `<details class="dataset-files"><summary>Included files (${doc.files.length})</summary><ul>${doc.files.map(f => `<li><b>${esc(f.path)}</b> · ${f.rowCount.toLocaleString()} rows · ${f.byteLength.toLocaleString()} bytes${Object.keys(f.partitions).length ? ` · ${esc(JSON.stringify(f.partitions))}` : ''}</li>`).join('')}</ul></details>` : '';
const excludedBlock = doc => doc.excludedPaths?.length ? `<details class="dataset-excluded"><summary>Excluded non-Parquet files (${doc.excludedPaths.length})</summary><ul>${doc.excludedPaths.map(path => `<li>${esc(path)}</li>`).join('')}</ul></details>` : '';
const partitionNote = doc => doc.partitionColumns?.length ? `<p class="viewer-note">Partition columns: ${doc.partitionColumns.map(esc).join(', ')}. Stored columns take precedence; missing columns use folder values.</p>` : '';
const schemaBlock = doc => `<details class="dataset-schema"><summary>Schema (${doc.schema.length} fields)</summary><ul>${doc.schema.map(c => `<li><b>${esc(c.name)}</b> — ${esc(c.logical_type ? JSON.stringify(c.logical_type) : c.converted_type || c.type || 'group')} · ${esc(c.repetition_type || '')}</li>`).join('')}</ul></details>`;
const filterField = `<label class="viewer-search">Filter rows <input type="search" data-filter aria-label="Filter Parquet rows" placeholder="Search values"></label>`;
const grid = doc => `<div class="viewer-table${doc.rowCount > PAGE_SIZE ? ' fixed-rows' : ''}" tabindex="0" role="region" aria-label="Parquet file rows"></div>`;
const pager = `<div class="viewer-controls"><button type="button" data-page="prev">Previous</button><span role="status"></span><button type="button" data-page="next">Next</button></div>`;
const exporters = `<div class="viewer-controls"><button type="button" data-export="json" aria-label="Download rows as JSON">Download JSON</button><button type="button" data-export="csv" aria-label="Download rows as CSV">Download CSV</button></div>`;
// Same controls, named as a group, because the workbench toolbar is a row of
// unrelated things and an unlabelled pair of buttons reads as a guess.
const exportGroup = `<div class="wb-group"><span class="wb-group-label" id="wbExportLabel">Download</span><div class="viewer-controls" role="group" aria-labelledby="wbExportLabel"><button type="button" data-export="json" aria-label="Download rows as JSON">JSON</button><button type="button" data-export="csv" aria-label="Download rows as CSV">CSV</button></div></div>`;
const VALUE_NOTE = '64-bit integers and decimals are exact strings; timestamps retain their original precision. Downloads contain decoded rows.';

// Inside a comparison pane: a compact stack, because the pane is one of two.
const paneChrome = doc => `
    <div class="file-viewer-meta">${metaLine(doc)}</div>
    ${filesBlock(doc)}${excludedBlock(doc)}${partitionNote(doc)}${schemaBlock(doc)}
    ${filterField}
    ${grid(doc)}
    ${pager}
    ${exporters}
    <p class="viewer-note" data-export-scope></p>
    <p class="viewer-note">${VALUE_NOTE}</p>`;

// On its own page: a full-viewport workbench — column rail, fixed grid, status
// bar — so paging and filtering never move the layout under the reader.
const workbenchChrome = doc => `
  <div class="wb">
    <aside class="wb-side" aria-label="Dataset and columns">
      <section class="wb-panel" aria-labelledby="wbColumnsHead">
        <h3 class="wb-panel-head" id="wbColumnsHead">Columns <span class="wb-panel-count"><b data-visible-count>${doc.columns.length}</b>/${doc.columns.length}</span>
          <span class="wb-side-acts"><button type="button" data-cols="all">All</button><button type="button" data-cols="none">None</button></span></h3>
        <label class="wb-colfind"><span class="sr-only">Find a column</span><input type="search" data-colfind placeholder="Find a column…" aria-label="Find a column"></label>
        <ul class="wb-cols">${doc.columns.map(c => `<li data-col-row="${esc(c)}"><label><input type="checkbox" data-col="${esc(c)}" checked><span class="wb-col-name">${esc(c)}</span></label><span class="wb-col-type">${esc(typeOf(doc.schema, c))}</span></li>`).join('')}</ul>
      </section>
      <section class="wb-panel wb-side-info" aria-labelledby="wbDatasetHead">
        <h3 class="wb-panel-head" id="wbDatasetHead">Dataset</h3>
        ${filesBlock(doc)}${excludedBlock(doc)}${partitionNote(doc)}${schemaBlock(doc)}
        <p class="viewer-note">${VALUE_NOTE}</p>
      </section>
      <dl class="wb-stats" aria-label="Dataset size">
        ${doc.files ? `<div><dt>Files</dt><dd>${doc.files.length}</dd></div>` : ''}
        <div><dt>Row groups</dt><dd>${doc.rowGroups}</dd></div>
        <div><dt>Rows</dt><dd>${doc.rowCount.toLocaleString()}</dd></div>
        <div><dt>Columns</dt><dd>${doc.columns.length}</dd></div>
        <div><dt>Codecs</dt><dd>${esc(doc.codecs.join(', ') || 'none')}</dd></div>
        ${Number.isFinite(doc.byteLength) ? `<div><dt>On disk</dt><dd>${bytes(doc.byteLength)}</dd></div>` : ''}
      </dl>
    </aside>
    <div class="wb-main">
      <div class="wb-toolbar">
        <button type="button" class="wb-side-toggle" data-side aria-expanded="true" aria-label="Toggle the columns panel">&#9776;</button>
        ${filterField}
        ${exportGroup}
        <button type="button" class="wb-close" data-close>Close file</button>
      </div>
      <div class="wb-meta">${metaLine(doc)}</div>
      ${grid(doc)}
      <div class="wb-status">
        <span class="wb-status-group">${pager}</span>
        <span class="wb-status-group"><label class="wb-jump">Jump to row <input type="number" data-jump min="1" max="${doc.rowCount}" aria-label="Jump to row"><button type="button" data-jump-go>Go</button></label><button type="button" data-jump-to="start">Start</button><button type="button" data-jump-to="end">End</button></span>
        <span class="wb-scope" data-export-scope></span>
        <span class="wb-count" data-count></span>
      </div>
    </div>
  </div>`;

/**
 * Paginated file/dataset viewer; it does not need a second comparison input.
 * `variant` picks the chrome only — filtering, paging, column visibility and
 * exports are one implementation, so the pane and the page cannot drift apart.
 */
export function mountFileViewer(container, doc, { variant = 'pane', onClose, onExport } = {}) {
  let page = 0;
  const indexedRows = doc.rows.map((row, index) => ({ row, index }));
  let rows = indexedRows;
  const hidden = new Set();
  const shown = () => doc.columns.filter(c => !hidden.has(c));
  const workbench = variant === 'workbench';

  container.innerHTML = workbench ? workbenchChrome(doc) : paneChrome(doc);
  const $ = (sel) => container.querySelector(sel);
  const table = $('.viewer-table');

  // Whether the rail shows BY DEFAULT is a question about width, so CSS answers
  // it (SIDEBAR_MAX_WIDTH in lib/responsive.js, mirrored in viewer-page.css and
  // asserted by a test). JS only records an explicit user override, which means
  // a resize or a rotation re-applies the right default with no listener to
  // miss — sampling the width once at mount left the rail over the grid on a
  // phone until something else re-rendered.
  const sideOpen = () => {
    // An explicit override wins, exactly as it does in the stylesheet; with no
    // override the rendered state is the answer, which is the width's default.
    const wb = $('.wb');
    if (wb.classList.contains('side-open')) return true;
    if (wb.classList.contains('side-closed')) return false;
    const computed = typeof getComputedStyle === 'function' ? getComputedStyle($('.wb-side')).display : '';
    return computed ? computed !== 'none' : true;
  };
  const syncSide = () => $('[data-side]')?.setAttribute('aria-expanded', String(sideOpen()));
  function toggleSide() {
    const open = sideOpen();
    $('.wb').classList.toggle('side-open', !open);
    $('.wb').classList.toggle('side-closed', open);
    syncSide();
  }
  // The VISIBLE rail is pure CSS and always right. `aria-expanded` cannot be, so
  // it is re-read whenever the width may have changed what is on screen — both
  // signals, because a container query and a window event fail in different
  // environments and a screen reader should never be told the opposite of what
  // is rendered.
  const observer = workbench && typeof ResizeObserver === 'function' ? new ResizeObserver(syncSide) : null;
  if (workbench) {
    observer?.observe($('.wb'));
    addEventListener?.('resize', syncSide);
    syncSide();
  }

  const scopeText = () => {
    const cols = shown().length;
    const colNote = cols === doc.columns.length ? '' : ` Visible columns only (${cols} of ${doc.columns.length}).`;
    return (rows.length === indexedRows.length
      ? `Downloads include all ${indexedRows.length.toLocaleString()} rows.`
      : `Downloads include the ${rows.length.toLocaleString()} filtered ${rows.length === 1 ? 'row' : 'rows'} (of ${indexedRows.length.toLocaleString()}), not just this page.`) + colNote;
  };

  const render = ({ toTop = false } = {}) => {
    const cols = shown();
    const start = page * PAGE_SIZE;
    const slice = rows.slice(start, start + PAGE_SIZE);
    table.innerHTML = `<table><thead><tr><th scope="col">Row</th>${cols.map(c => `<th scope="col">${esc(c)}</th>`).join('')}</tr></thead><tbody>${slice.map(({ row, index }) => `<tr><th scope="row">${index + 1}</th>${cols.map(c => `<td>${esc(display(row[c]))}</td>`).join('')}</tr>`).join('')}</tbody></table>${rows.length ? '' : '<p>No rows to display.</p>'}`;
    // A new page starts at its first row; keeping the old scroll offset lands the
    // reader in the middle of rows they have not seen.
    if (toTop) table.scrollTop = 0;
    $('[role="status"]').textContent = rows.length ? `${start + 1}–${Math.min(start + PAGE_SIZE, rows.length)} of ${rows.length}` : '0 rows';
    $('[data-page="prev"]').disabled = page === 0;
    $('[data-page="next"]').disabled = start + PAGE_SIZE >= rows.length;
    // Say exactly what a download will contain: every matching row and every
    // shown column, never the visible page. "Download", not "Export": the file is
    // written by this tab, so the word that describes it is the one the browser uses.
    $('[data-export-scope]').textContent = scopeText();
    if (workbench) {
      $('[data-visible-count]').textContent = String(cols.length);
      $('[data-count]').textContent = `${rows.length.toLocaleString()} ${rows.length === 1 ? 'row' : 'rows'} × ${cols.length} ${cols.length === 1 ? 'col' : 'cols'}`;
    }
  };

  const applyFilter = () => {
    const query = ($('[data-filter]').value || '').toLowerCase();
    const cols = shown();
    rows = query ? indexedRows.filter(({ row }) => cols.some(c => display(row[c]).toLowerCase().includes(query))) : indexedRows;
    page = 0;
  };

  const onInput = event => {
    const target = event.target;
    if (target.matches('[data-colfind]')) {
      const q = target.value.toLowerCase();
      for (const li of container.querySelectorAll('[data-col-row]')) li.hidden = !!q && !li.dataset.colRow.toLowerCase().includes(q);
      return;
    }
    if (target.matches('[data-col]')) {
      hidden[target.checked ? 'delete' : 'add'](target.dataset.col);
      applyFilter();
      render({ toTop: true });
      return;
    }
    if (target.matches('[data-jump]')) return; // acted on via Go, not per keystroke
    if (!target.matches('input[type="search"], [data-filter]')) return;
    applyFilter();
    render({ toTop: true });
  };

  const jumpTo = rowNumber => {
    if (!Number.isFinite(rowNumber)) return;
    const pos = rows.findIndex(({ index }) => index + 1 >= rowNumber);
    page = pos < 0 ? Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1) : Math.floor(pos / PAGE_SIZE);
    render({ toTop: true });
  };

  const onClick = event => {
    const button = event.target.closest('button');
    if (!button) return;
    if (button.dataset.page) { page += button.dataset.page === 'next' ? 1 : -1; render({ toTop: true }); }
    if (button.dataset.cols) {
      hidden.clear();
      if (button.dataset.cols === 'none') for (const c of doc.columns) hidden.add(c);
      for (const box of container.querySelectorAll('[data-col]')) box.checked = !hidden.has(box.dataset.col);
      applyFilter();
      render({ toTop: true });
    }
    if (button.hasAttribute('data-jump-go')) jumpTo(Number($('[data-jump]').value));
    if (button.dataset.jumpTo === 'start') { page = 0; render({ toTop: true }); }
    if (button.dataset.jumpTo === 'end') { page = Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1); render({ toTop: true }); }
    if (button.hasAttribute('data-side')) toggleSide();
    if (button.hasAttribute('data-close')) onClose?.();
    const cols = shown();
    const name = rows.length !== indexedRows.length ? 'parquet-rows-filtered' : 'parquet-rows';
    if (button.dataset.export === 'json') download(`${name}.json`, JSON.stringify(rows.map(({ row }) => Object.fromEntries(cols.map(c => [c, row[c]]))), null, 2), 'application/json');
    if (button.dataset.export === 'csv') download(`${name}.csv`, Papa.unparse({ fields: cols, data: rows.map(({ row }) => cols.map(c => display(row[c]))) }, { escapeFormulae: true }), 'text/csv');
    // Scope only — whether the file held every row or the filtered set. Never the
    // filter text, the column selection, the row count or the file name.
    if (button.dataset.export) onExport?.(rows.length !== indexedRows.length ? 'filtered' : 'all');
  };

  container.addEventListener('input', onInput);
  container.addEventListener('click', onClick);
  render();
  return () => {
    container.removeEventListener('input', onInput);
    container.removeEventListener('click', onClick);
    observer?.disconnect();
    if (workbench) removeEventListener?.('resize', syncSide);
    container.innerHTML = '';
  };
}
